import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import Tooltip from 'components/tooltip';

export type PreviewEyeProps = {
  onClick?: () => void;
  isHidden?: boolean;
}

function PreviewEye({ onClick, isHidden = false }: PreviewEyeProps) {
  const icon = isHidden ? faEyeSlash : faEye;

  return (
    <Tooltip
      content={isHidden ? 'This shows the card from the report' : 'This hides the card from the report'}
      dark={true}
    >
      <div
        className={`ds-card-preview ${isHidden ? 'ds-preview-hidden' : ''}`}
        onClick={onClick}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
    </Tooltip>
  );
}

interface WithPreviewEyeProps {
  displayCard?: boolean;
  onPreviewChange?: (isHidden: boolean) => void;
  preview?: boolean;
  [key: string]: any; // For additional props
}

export function withPreviewEye(WrappedComponent) {
  return function WithPreviewEye(props: WithPreviewEyeProps) {
    const {
      displayCard,
      onPreviewChange,
      preview,
      ...rest
    } = props;

    const [isHidden, setIsHidden] = useState(displayCard === false);

    useEffect(() => {
      setIsHidden(displayCard === false);
    }, [displayCard]);

    const handlePreviewClick = () => {
      const newHiddenState = !isHidden;
      setIsHidden(newHiddenState);

      if (onPreviewChange) {
        onPreviewChange(newHiddenState);
      }
    };

    // If card is hidden and we're in preview mode, don't render anything
    if (isHidden && preview) {
      return null;
    }

    const previewEyeComponent = (
      <PreviewEye
        isHidden={isHidden}
        onClick={handlePreviewClick}
      />
    );

    return (
      <div className={`position-relative h-100 ds-card-preview-${isHidden ? 'hidden' : 'visible'}`}>
        <WrappedComponent
          {...rest}
          isHidden={isHidden}
          preview={preview}
          previewEye={previewEyeComponent}
        />
      </div>
    );
  };
}

export default PreviewEye;
